import api from "@/services/api";
import type {
  TastingPanelListFilterKeywords,
  TastingPanelListRequest,
  TastingPanelListResponse,
  TastingPanelListSortOrder,
} from "@/components/TastingPanelList/types/tastingPanelListApi.types";
import { EMPTY_TASTING_PANEL_LIST_FILTER_KEYWORDS } from "@/components/TastingPanelList/types/tastingPanelListApi.types";
import { normalizeTastingPanelListResponse } from "@/components/TastingPanelList/utils/tastingPanelListApiTransform";

const { productServiceAPI } = api;

const TASTING_PANEL_ACTIVE_LIST_PATH = "/idea/tastingPanelList/active";
const TASTING_PANEL_ARCHIVED_LIST_PATH = "/idea/tastingPanelList/archived";

function normalizeStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x).trim()).filter(Boolean);
}

function normalizeFilterKeywordsForPost(
  fk: TastingPanelListFilterKeywords | undefined
): TastingPanelListFilterKeywords {
  const o = fk ?? EMPTY_TASTING_PANEL_LIST_FILTER_KEYWORDS;
  return {
    productType: normalizeStringArray(o.productType),
    businessStatus: normalizeStringArray(o.businessStatus),
    businessCategoryName: normalizeStringArray(o.businessCategoryName),
    categoryManager: normalizeStringArray(o.categoryManager),
    developer: normalizeStringArray(o.developer),
    vendor: normalizeStringArray(o.vendor),
  };
}

function buildListPostBody(payload: TastingPanelListRequest): TastingPanelListRequest {
  const sortOrder: TastingPanelListSortOrder = payload.sortOrder === "ASC" ? "ASC" : "DESC";

  return {
    pageNo: Math.max(0, Math.floor(Number(payload.pageNo) || 0)),
    pageSize: Math.max(1, Math.floor(Number(payload.pageSize) || 10)),
    search: typeof payload.search === "string" ? payload.search : "",
    sortBy: typeof payload.sortBy === "string" && payload.sortBy.trim() ? payload.sortBy.trim() : "id",
    sortOrder,
    filterKeywords: normalizeFilterKeywordsForPost(payload.filterKeywords),
  };
}

async function postTastingPanelList(
  path: string,
  payload: TastingPanelListRequest
): Promise<TastingPanelListResponse> {
  const body = buildListPostBody(payload);
  const resp = await productServiceAPI.post<unknown>(path, body);

  const totalHeader =
    (resp as { headers?: Record<string, string> })?.headers?.["x-total-count"] ??
    (resp as { headers?: Record<string, string> })?.headers?.["X-Total-Count"];

  return normalizeTastingPanelListResponse(resp.data, body, totalHeader);
}

export { normalizeTastingPanelListResponse } from "@/components/TastingPanelList/utils/tastingPanelListApiTransform";

export async function postTastingPanelActiveList(
  payload: TastingPanelListRequest
): Promise<TastingPanelListResponse> {
  return postTastingPanelList(TASTING_PANEL_ACTIVE_LIST_PATH, payload);
}

export async function postTastingPanelArchivedList(
  payload: TastingPanelListRequest
): Promise<TastingPanelListResponse> {
  return postTastingPanelList(TASTING_PANEL_ARCHIVED_LIST_PATH, payload);
}

const TastingPanelListService = {
  postActiveList: postTastingPanelActiveList,
  postArchivedList: postTastingPanelArchivedList,
};

export default TastingPanelListService;
